import { useEffect, useState } from "react";
import { MessageSquareQuote } from "lucide-react";
import { http, fmtDate } from "@/lib/api";
import { useT, useLoc } from "@/lib/i18n";
import { OTA } from "@/components/customer/PropertyExtras";

const Card = ({ r, t }) => (
  <article data-testid={`review-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="font-heading font-bold text-slate-900">{r.guest_name}{r.country ? ` · ${r.country}` : ""}</p>
        <p className="text-xs text-slate-500">{[r.room_name, r.stay_month && fmtDate(`${r.stay_month}-01`, "MMM yyyy")].filter(Boolean).join(" · ")}</p>
        {r.source && <span data-testid={`review-source-${r.id}`} className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">{t("via {s}", { s: OTA[r.source] })}</span>}
      </div>
      <span className="rounded-tl-lg rounded-br-lg bg-[#5392F9] px-2 py-1 text-sm font-bold text-white">{Number(r.rating).toFixed(r.source ? 1 : 0)}</span>
    </div>
    {r.title && <p className="mt-3 font-semibold text-slate-900">{r.title}</p>}
    <p className="mt-2 flex gap-2 whitespace-pre-line text-sm leading-relaxed text-slate-600"><MessageSquareQuote className="h-4 w-4 shrink-0 text-slate-300" />{r.comment}</p>
    {r.reply && (
      <div data-testid={`review-reply-${r.id}`} className="mt-4 border-l-4 border-[#1F5F8B] bg-slate-50 py-2 pl-3 pr-2">
        <p className="text-xs font-bold text-[#1F5F8B]">{t("Balasan dari properti")}</p>
        <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{r.reply}</p>
      </div>
    )}
  </article>
);

export default function ReviewsSection({ pid, ota = [] }) {
  const t = useT();
  const loc = useLoc();
  const [items, setItems] = useState(null);
  useEffect(() => {
    http.get(`/public/properties/${pid}/reviews`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [pid]);
  if (!items) return null;
  const otaItems = (ota || []).map((o, i) => ({ id: `ota-${i}`, source: o.source, guest_name: o.name, country: o.country, rating: o.score, comment: loc(o, "text"), stay_month: o.date }));
  if (!items.length && !otaItems.length) return null;
  const avg = items.length ? items.reduce((s, r) => s + r.rating, 0) / items.length : 0;
  return (
    <section className="mt-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-heading text-2xl font-bold text-slate-900">{t("Review tamu")}</h2>
        <p className="text-sm text-slate-600" data-testid="reviews-summary">
          {items.length > 0 && <><b className="rounded bg-[#5392F9] px-2 py-0.5 text-white">{avg.toFixed(1)}</b> · {t("{n} review terverifikasi", { n: items.length })}</>}
          {items.length > 0 && otaItems.length > 0 && " · "}
          {otaItems.length > 0 && t("{n} review pilihan dari OTA", { n: otaItems.length })}
        </p>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {[...items, ...otaItems].map((r) => <Card key={r.id} r={r} t={t} />)}
      </div>
    </section>
  );
}
